export function getPositiveSpreadBoost(line: number): number {
  return line > 0 ? line : 0;
}

export type SpreadBarResult = "covering" | "trailing" | "push";

export interface SpreadScoreBarModel {
  side: "away" | "home";
  label: string;
  score: number;
  spreadBoost: number;
  isPicked: boolean;
  fill: number;
}

export function buildSpreadScoreBars(input: {
  awayLabel: string;
  homeLabel: string;
  awayScore: number;
  homeScore: number;
  pickedSide: "away" | "home";
  pickedLine: number;
  coverTone: "positive" | "neutral" | "negative";
}): {
  scale: number;
  away: SpreadScoreBarModel;
  home: SpreadScoreBarModel;
  result: SpreadBarResult;
} {
  const awayLine = input.pickedSide === "away" ? input.pickedLine : -input.pickedLine;
  const homeLine = input.pickedSide === "home" ? input.pickedLine : -input.pickedLine;
  const awayBoost = getPositiveSpreadBoost(awayLine);
  const homeBoost = getPositiveSpreadBoost(homeLine);
  const awayFill = Math.max(0, input.awayScore) + awayBoost;
  const homeFill = Math.max(0, input.homeScore) + homeBoost;
  const result: SpreadBarResult = input.coverTone === "positive"
    ? "covering"
    : input.coverTone === "neutral"
      ? "push"
      : "trailing";

  return {
    scale: Math.max(awayFill, homeFill, 1),
    result,
    away: {
      side: "away",
      label: input.awayLabel,
      score: Math.max(0, input.awayScore),
      spreadBoost: awayBoost,
      isPicked: input.pickedSide === "away",
      fill: awayFill,
    },
    home: {
      side: "home",
      label: input.homeLabel,
      score: Math.max(0, input.homeScore),
      spreadBoost: homeBoost,
      isPicked: input.pickedSide === "home",
      fill: homeFill,
    },
  };
}

export type SpreadAxisMarkAlign = "start" | "center" | "end";

export interface SpreadAxisMark {
  key: "origin" | "away" | "home";
  percent: number;
  label: string;
  align: SpreadAxisMarkAlign;
}

function formatAxisScore(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function buildSpreadScoreAxisMarks(
  away: SpreadScoreBarModel,
  home: SpreadScoreBarModel,
  scale: number,
): SpreadAxisMark[] {
  const safeScale = scale > 0 ? scale : 1;
  const scoreMarks: SpreadAxisMark[] = [];

  if (away.fill > 0) {
    scoreMarks.push({
      key: "away",
      percent: Math.min(100, (away.fill / safeScale) * 100),
      label: formatAxisScore(away.fill),
      align: "center",
    });
  }
  if (home.fill > 0) {
    scoreMarks.push({
      key: "home",
      percent: Math.min(100, (home.fill / safeScale) * 100),
      label: formatAxisScore(home.fill),
      align: "center",
    });
  }

  scoreMarks.sort((first, second) => first.percent - second.percent);
  const leftScore = scoreMarks[0];
  const rightScore = scoreMarks[1];
  if (leftScore && rightScore && Math.abs(leftScore.percent - rightScore.percent) < 12) {
    leftScore.align = "end";
    rightScore.align = "start";
  }
  for (const mark of scoreMarks) {
    if (mark.percent >= 97) mark.align = "end";
    else if (mark.percent <= 3) mark.align = "start";
  }

  const showOrigin = scoreMarks.every((mark) => mark.percent > 8);
  if (!showOrigin) return scoreMarks;
  return [
    { key: "origin", percent: 0, label: "0", align: "start" },
    ...scoreMarks,
  ];
}
